import { useEffect, useState } from "react";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Section, Field, Choice } from "./formBits";
import { getIdPhotoUrl } from "@/lib/employeeApi";

export const STATUS_OPTIONS = [["active", "Active"], ["on_leave", "On leave"], ["inactive", "Inactive"]];
const PRIMARY_ADMIN = "admin@primemediawireless.com";

const IdPhoto = ({ userId, hasPhoto, onFile, disabled }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => { if (userId && hasPhoto) getIdPhotoUrl(userId).then(setUrl).catch(() => {}); }, [userId, hasPhoto]);
  return (
    <div className="sm:col-span-2 flex items-center gap-3">
      {url && <a href={url} target="_blank" rel="noreferrer" data-testid="user-id-photo-link"><img src={url} alt="ID" className="h-16 rounded border border-slate-200 object-cover" /></a>}
      {!disabled && <div className="flex-1"><Label className="text-xs">{hasPhoto ? "Replace ID photo" : "ID photo"}</Label>
        <Input type="file" accept="image/*,application/pdf" className="mt-1" onChange={(e) => onFile(e.target.files?.[0] || null)} data-testid="user-id-photo-input" /></div>}
    </div>
  );
};

export const Credentials = ({ form, set, ro, editing, roles, meSuper, isSelfView }) => (
  <Section title="Login & security role" testId="user-section-credentials">
    <Field label="Work email (login)" path="email" type="email" form={form} set={set} disabled={ro} required />
    {!isSelfView && <Field label={editing ? "New password (leave blank to keep)" : "Password"} path="password" type="password"
      form={form} set={set} disabled={ro} required={!editing} />}
    {!form.super_admin && !isSelfView && (
      <div className="sm:col-span-2">
        <Label className="text-xs">Security role *</Label>
        <Select value={form.role_id || ""} onValueChange={(v) => set("role_id", v)} disabled={ro}>
          <SelectTrigger className="mt-1" data-testid="user-role-select"><SelectValue placeholder="Pick a security role" /></SelectTrigger>
          <SelectContent>{roles.map((r) => <SelectItem key={r.id} value={r.id} data-testid={`user-role-option-${r.id}`}>{r.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    )}
    {meSuper && !ro && (
      <label className="sm:col-span-2 flex items-start gap-3 p-3 rounded-lg border border-amber-200 bg-amber-50" data-testid="super-admin-toggle-row">
        <Checkbox checked={!!form.super_admin} disabled={form.email === PRIMARY_ADMIN && editing}
          onCheckedChange={(v) => set("super_admin", !!v)} data-testid="super-admin-toggle" />
        <span className="text-sm text-amber-900"><b>Super Admin</b> — always has every permission. Only Super Admins can edit this person.</span>
      </label>
    )}
  </Section>
);

export const GeneralTab = ({ form, set, ro, userId, hasPhoto, onPhoto, unmasked }) => (
  <div className="space-y-6">
    <Section title="Personal information" testId="user-section-personal">
      <Field label="First name" path="first_name" form={form} set={set} disabled={ro} required />
      <Field label="Last name" path="last_name" form={form} set={set} disabled={ro} required />
      <Field label="Street address" path="profile.address.street" form={form} set={set} disabled={ro} wide />
      <Field label="City" path="profile.address.city" form={form} set={set} disabled={ro} />
      <div className="grid grid-cols-2 gap-3">
        <Field label="State" path="profile.address.state" form={form} set={set} disabled={ro} />
        <Field label="ZIP" path="profile.address.zip" form={form} set={set} disabled={ro} />
      </div>
      <Field label="Phone number" path="phone" form={form} set={set} disabled={ro} />
      <Field label="Personal email" path="profile.personal_email" type="email" form={form} set={set} disabled={ro} />
      <Field label="Date of birth" path="profile.dob" type="date" form={form} set={set} disabled={ro} />
    </Section>
    <Section title="Emergency contact" testId="user-section-emergency">
      <Field label="Name" path="profile.emergency_contact.name" form={form} set={set} disabled={ro} />
      <Field label="Relationship" path="profile.emergency_contact.relationship" form={form} set={set} disabled={ro} />
      <Field label="Phone" path="profile.emergency_contact.phone" form={form} set={set} disabled={ro} />
    </Section>
    <Section title="ID" testId="user-section-id">
      <Field label="ID type" path="id_doc.type" form={form} set={set} disabled={ro} placeholder="Driver license, State ID, Passport…" />
      <Field label={`ID number${unmasked ? "" : " (last 4 shown)"}`} path="id_doc.number" form={form} set={set} disabled={ro} />
      <Field label="Issuing state" path="id_doc.state" form={form} set={set} disabled={ro} />
      <Field label="Expiration date" path="id_doc.expiry" type="date" form={form} set={set} disabled={ro} />
      <IdPhoto userId={userId} hasPhoto={hasPhoto} onFile={onPhoto} disabled={ro} />
    </Section>
    <Section title="Employee status" testId="user-section-status">
      <Choice label="Status" path="employment_status" form={form} set={set} options={STATUS_OPTIONS} disabled={ro} testId="user-status" />
      {form.employment_status === "inactive" && (
        <Field label="Separation date" path="hr.separation_date" type="date" form={form} set={set} disabled={ro} required />
      )}
      <p className="sm:col-span-2 text-xs text-slate-400">Only Active employees can sign in to the portal.</p>
    </Section>
  </div>
);
